import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { environment } from '../../../../environments/environment';
import { FileShareModel } from '../../../models/file-share.model';
import { AlertService } from '../../../services/alert.service';
import { AuthService } from '../../../services/auth.service';
import { EnvironmentService } from '../../../services/environment.service';
import { FileSharesResource } from '../../../services/resources/file-shares.resource';
import { AppSessionStorage } from '../../../services/storage/app.session-storage';
import { SecurityStorage } from '../../../services/storage/security.storage';
import { FileSharingFormComponent } from './form/file-sharing-form.component';

@Component({
    selector: 'sl-file-sharing',
    templateUrl: './file-sharing.component.html',
    styleUrls: ['./file-sharing.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FileSharingComponent implements OnInit {
    fileShares: FileShareModel[] = [];
    displayedColumns: string[] = ['fileName', 'createdByUserUsername', 'expiresByUtc', 'action'];
    ctx: string;
    token: string;
    canUpload = false;

    private readonly dialog = inject(MatDialog);
    private readonly fileSharesResource = inject(FileSharesResource);
    private readonly alertService = inject(AlertService);
    private readonly appSessionStorage = inject(AppSessionStorage);
    private readonly securityStorageService = inject(SecurityStorage);
    private readonly environmentService = inject(EnvironmentService);
    private readonly authService = inject(AuthService);

    ngOnInit(): void {
        this.fileSharesResource.getAll().subscribe(r => (this.fileShares = r.value));

        this.ctx = this.appSessionStorage.getCurrentContext();
        this.token = this.securityStorageService.getUserInfo()?.token ?? '';
        this.canUpload = this.authService.hasPermission('CreateFileShareCommand');
    }

    addFile(): void {
        const ref = this.dialog.open(FileSharingFormComponent, { width: '440px' });
        ref.afterClosed().subscribe(id => {
            if (!!id) {
                this.fileSharesResource.get(id).subscribe(resp => {
                    this.fileShares.unshift(resp.value);
                    this.updateFilesList();
                });
            }
        });
    }

    removeFile(id: string): void {
        this.alertService.showConfirm('General.AreYouSureRemove', undefined, () => {
            this.fileSharesResource.remove(id).subscribe(() => {
                const idx = this.fileShares.findIndex(i => i.id === id);
                this.fileShares.splice(idx, 1);
                this.updateFilesList();
            });
        });
    }

    getDownloadUrl(item: FileShareModel): string {
        const apiKey = environment.useAirlock ? '' : `api_key=${this.token}&`;
        return `${this.environmentService.baseUrl}/${item.downloadUrl}?${apiKey}ctx=${this.ctx}&filename=${item.fileName}`;
    }

    getFileSymbol(fileName: string): string {
        return fileName.endsWith('.pdf') ? 'picture_as_pdf' : 'attachment';
    }

    private updateFilesList(): void {
        this.fileShares = [...this.fileShares]; // Required to trigger the view change.
    }
}
